import { PickAnAttribute } from './PickAnAttribute.js';
import { GoogleGeometry } from './GoogleGeometry.js';
import { calcTotalLinearWeight } from './wcs-english/WorstCaseSagCalcs.js';

/**
 * The marker attributes that describe the scenario, and the defaults used when they are absent.
 * The surveyed condition is always bare wire, so it has no ice, wind, or K of its own.
 */
const SCENARIO_DEFAULTS = { ice: 0, wind: 0, k: 0, wireCondition: 'Final' };

/**
 * Details about how a scenario sag move was produced
 * @typedef {Object} ScenarioSagMoveDetails
 * @property {string} wireSpec - The wire spec the tensions were read for.
 * @property {number} rulingSpan - The ruling span the rows were chosen at (feet).
 * @property {number} spanLength - The length of the span the catenaries were hung over (feet).
 * @property {number} sectionDistance - How far along the span the move was evaluated (feet).
 * @property {ScenarioSagConditionDetails} surveyed - The surveyed condition's row and sag.
 * @property {ScenarioSagConditionDetails} scenario - The scenario condition's row and sag.
 */

/**
 * One condition's contribution to the move.
 * @typedef {Object} ScenarioSagConditionDetails
 * @property {number} temperature - The condition's temperature (°F).
 * @property {number} ice - The condition's radial ice thickness (in).
 * @property {number} wind - The condition's horizontal wind pressure (lb/ft²).
 * @property {number} k - The condition's NESC K constant (lb/ft).
 * @property {'Initial'|'Final'} wireCondition - Which tension column the row was read from.
 * @property {number} tension - The horizontal tension the row gave (lb).
 * @property {number|string} rulingSpanEntry - The row used: its name when it has one, its ruling span otherwise.
 * @property {number} loadedWeight - The wire's linear weight with the condition's ice, wind, and K added (lb/ft).
 * @property {number} sag - The sag below the attachments at the section (feet).
 */

/**
 * Calculates the effective move that puts a midspan marker where the wire would hang under a weather change
 *
 * @param {Object} options
 * @param {Object} options.marker - The midspan marker, carrying the wire spec and the scenario attributes.
 * @param {Object} [options.sagTables] - The model's sag tables, keyed by wire spec key (`models/sag_tables`).
 * @param {Object} [options.sagWireSpecs] - Linear weight (lb/ft) and diameter (in) per wire spec key, as
 *   `{ wire_spec, weight, diameter }` entries.
 * @param {number} options.spanLength - The length of the span the section is in (feet).
 * @param {number} options.sectionDistance - How far the section is along that span from its first pole (feet).
 * @returns {{move: number, error: ?string, details: ?ScenarioSagMoveDetails}} The move in inches, or error
 */
export function CalcScenarioSagMove({ marker, sagTables, sagWireSpecs, spanLength, sectionDistance }) {
  const noMove = { move: 0, error: null, details: null };

  const scenarioTemperature = readNumericAttribute(marker, 'scenario_temperature');
  if (scenarioTemperature == null) return noMove;
  const surveyedTemperature = readNumericAttribute(marker, 'wire_temperature');
  if (surveyedTemperature == null) return { ...noMove, error: 'Scenario sag: the marker has no surveyed wire temperature.' };

  const wireSpec = readAttribute(marker, 'wire_spec');
  if (!wireSpec) return { ...noMove, error: 'Scenario sag: the marker has no wire spec.' };

  // Sag tables and wire specs are both keyed by wire spec key and carry the spec's name; the marker has the name.
  const rows = Object.values(sagTables ?? {}).find((entry) => entry?.wire_spec == wireSpec)?.sag_table;
  if (rows == null) return { ...noMove, error: `Scenario sag: no sag table for the ${wireSpec} wire spec.` };

  const spec = Object.values(sagWireSpecs ?? {}).find((entry) => entry?.wire_spec == wireSpec);
  const linearWeight = toNumberOrNull(spec?.weight);
  if (!(linearWeight > 0)) return { ...noMove, error: `Scenario sag: no linear weight for the ${wireSpec} wire spec.` };
  const diameter = toNumberOrNull(spec?.diameter);

  if (!(spanLength > 0)) return { ...noMove, error: 'Scenario sag: the span has no length.' };
  if (!(sectionDistance >= 0 && sectionDistance <= spanLength))
    return { ...noMove, error: 'Scenario sag: the section is not within the span.' };
  const rulingSpan = readNumericAttribute(marker, 'assigned_ruling_span');
  if (!(rulingSpan > 0)) return { ...noMove, error: 'Scenario sag: the marker has no assigned ruling span.' };

  // The surveyed condition is the wire as it was found: bare, at the temperature the crew recorded.
  const surveyed = {
    temperature: surveyedTemperature,
    ice: SCENARIO_DEFAULTS.ice,
    wind: SCENARIO_DEFAULTS.wind,
    k: SCENARIO_DEFAULTS.k,
    wireCondition: readWireCondition(marker, 'wire_condition')
  };
  const scenario = {
    temperature: scenarioTemperature,
    ice: readNumericAttribute(marker, 'scenario_ice') ?? SCENARIO_DEFAULTS.ice,
    wind: readNumericAttribute(marker, 'scenario_wind') ?? SCENARIO_DEFAULTS.wind,
    k: readNumericAttribute(marker, 'scenario_k_factor') ?? SCENARIO_DEFAULTS.k,
    wireCondition: readWireCondition(marker, 'scenario_condition')
  };

  const tabulatedRulingSpan = pickTabulatedRulingSpan(rows, rulingSpan);
  if (tabulatedRulingSpan == null) return { ...noMove, error: `Scenario sag: the ${wireSpec} sag table has no usable rows.` };

  const surveyedRow = findRowForCondition(rows, surveyed, tabulatedRulingSpan);
  if (surveyedRow == null)
    return { ...noMove, error: `Scenario sag: ${describeMissingRow(wireSpec, 'surveyed', surveyed, tabulatedRulingSpan)}` };
  const scenarioRow = findRowForCondition(rows, scenario, tabulatedRulingSpan);
  if (scenarioRow == null)
    return { ...noMove, error: `Scenario sag: ${describeMissingRow(wireSpec, 'scenario', scenario, tabulatedRulingSpan)}` };

  // Ice and wind loads scale with the wire's diameter, so a bare condition is the only one that can do without it
  if ((scenario.ice > 0 || scenario.wind > 0) && !(diameter > 0))
    return { ...noMove, error: `Scenario sag: no diameter for the ${wireSpec} wire spec, which ice and wind need.` };

  // Each row's tension was solved for the wire carrying that row's ice, wind, and K, so the sag must use the same weight
  const surveyedWeight = calcLoadedWeight(linearWeight, diameter, surveyed);
  const scenarioWeight = calcLoadedWeight(linearWeight, diameter, scenario);
  const surveyedSag = calcLevelSpanSagAt(surveyedRow.tension, surveyedWeight, spanLength, sectionDistance);
  const scenarioSag = calcLevelSpanSagAt(scenarioRow.tension, scenarioWeight, spanLength, sectionDistance);
  if (!Number.isFinite(surveyedSag) || !Number.isFinite(scenarioSag))
    return { ...noMove, error: `Scenario sag: the ${wireSpec} catenary could not be solved.` };

  // Sagging further in the scenario than when surveyed moves the wire down, so the move is negative.
  const move = (surveyedSag - scenarioSag) * 12;

  return {
    move,
    error: null,
    details: {
      wireSpec,
      rulingSpan,
      tabulatedRulingSpan,
      spanLength,
      sectionDistance,
      surveyed: { ...surveyed, ...surveyedRow, loadedWeight: surveyedWeight, sag: surveyedSag },
      scenario: { ...scenario, ...scenarioRow, loadedWeight: scenarioWeight, sag: scenarioSag }
    }
  };
}

/**
 * Gathers the data CalcScenarioSagMove needs to hang a section's midspan markers under a weather change
 * @param {Object} options
 * @param {?Object} options.sagTables - The model's sag tables, keyed by wire spec key.
 * @param {?Object} options.sagWireSpecs - The linear weight and diameter of each wire spec, keyed by wire spec key.
 * @param {?Object} options.node1 - The node at one end of the section's connection.
 * @param {?Object} options.node2 - The node at the other end.
 * @param {?Object} options.section - The section the midspan markers are in.
 * @returns {?{sagTables: Object, sagWireSpecs: Object, spanLength: number, sectionDistance: number}} The scenario sag data,
 *   or null when something the math needs is missing.
 */
export function BuildScenarioSagData({ sagTables, sagWireSpecs, node1, node2, section }) {
  if (!sagTables || !sagWireSpecs) return null;
  if (node1?.latitude == null || node2?.latitude == null || section?.latitude == null) return null;

  const pole1LatLng = new GoogleGeometry.maps.LatLng(node1.latitude, node1.longitude);
  const pole2LatLng = new GoogleGeometry.maps.LatLng(node2.latitude, node2.longitude);
  const sectionLatLng = new GoogleGeometry.maps.LatLng(section.latitude, section.longitude);
  // Sag tables hold feet, and the spherical geometry gives metres.
  const spanLength = GoogleGeometry.maps.geometry.spherical.computeDistanceBetween(pole1LatLng, pole2LatLng) / 0.3048;
  // The sag is symmetric along a level span, so either pole can be measured from.
  const sectionDistance = GoogleGeometry.maps.geometry.spherical.computeDistanceBetween(pole1LatLng, sectionLatLng) / 0.3048;
  if (!(spanLength > 0)) return null;

  return { sagTables, sagWireSpecs, spanLength, sectionDistance };
}

/**
 * Normalizes a sag table's rows
 * @param {Object} rows - The wire spec's sag table rows, keyed by row id.
 * @returns {Array<Object>} The usable rows.
 */
function normalizeRows(rows) {
  return Object.values(rows ?? {})
    .map((row) => ({
      rulingSpan: toNumberOrNull(row?.ruling_span),
      temperature: toNumberOrNull(row?.temperature),
      ice: toNumberOrNull(row?.ice) ?? 0,
      wind: toNumberOrNull(row?.wind) ?? 0,
      k: toNumberOrNull(row?.k) ?? 0,
      initial_tension: toNumberOrNull(row?.initial_tension),
      final_tension: toNumberOrNull(row?.final_tension),
      name: row?.name ?? null
    }))
    .filter((row) => row.rulingSpan != null);
}

/**
 * Picks which tabulated ruling span to read both conditions out of
 * @param {Object} rows - The wire spec's sag table rows, keyed by row id.
 * @param {number} rulingSpan - The conductor's ruling span (feet).
 * @returns {?number} The tabulated ruling span, or null when the table has no usable row.
 */
function pickTabulatedRulingSpan(rows, rulingSpan) {
  const tabulated = [...new Set(normalizeRows(rows).map((row) => row.rulingSpan))].sort((a, b) => a - b);
  return tabulated.find((tabulatedSpan) => tabulatedSpan >= rulingSpan) ?? tabulated.at(-1) ?? null;
}

/**
 * Finds the tension for a condition among the rows at one tabulated ruling span. The condition's temperature,
 * ice, wind, and K must all match a row exactly
 * @param {Object} rows - The wire spec's sag table rows, keyed by row id.
 * @param {{temperature: number, ice: number, wind: number, k: number, wireCondition: string}} condition - The condition to match.
 * @param {number} tabulatedRulingSpan - The tabulated ruling span to read from (feet).
 * @returns {?{tension: number, rulingSpanEntry: number|string}} The row's tension and the row's identifier, or null.
 */
function findRowForCondition(rows, condition, tabulatedRulingSpan) {
  const tensionKey = condition.wireCondition == 'Initial' ? 'initial_tension' : 'final_tension';

  const row = normalizeRows(rows).find(
    (row) =>
      row.rulingSpan === tabulatedRulingSpan &&
      row[tensionKey] > 0 &&
      row.temperature === condition.temperature &&
      row.ice === condition.ice &&
      row.wind === condition.wind &&
      row.k === condition.k
  );
  if (row == null) return null;

  // Rows can share a ruling span, so a row is identified by its name when it has one.
  return { tension: row[tensionKey], rulingSpanEntry: row.name || row.rulingSpan };
}

/**
 * Calculates the wire's linear weight under a condition: the bare weight and the ice weight combined with the wind
 * load, plus the NESC K constant
 * @param {number} linearWeight - The bare wire's linear weight (lb/ft).
 * @param {?number} diameter - The wire's diameter (in). Only needed when the condition has ice or wind.
 * @param {{ice: number, wind: number, k: number}} condition - The condition's radial ice (in), wind pressure (lb/ft²), and K (lb/ft).
 * @returns {number} The loaded linear weight (lb/ft).
 */
function calcLoadedWeight(linearWeight, diameter, condition) {
  return calcTotalLinearWeight(
    { linearWeight, diameter: diameter ?? 0 },
    { iceRadius: condition.ice, windPressure: condition.wind, conductorConstant: condition.k }
  );
}

/**
 * Calculates how far a level span's wire hangs below its attachments at a point along the span.
 * @param {number} tension - The horizontal tension (lb).
 * @param {number} linearWeight - The linear weight of the wire (lb/ft).
 * @param {number} spanLength - The length of the span (feet).
 * @param {number} distance - How far along the span to evaluate, from either attachment (feet).
 * @returns {number} The sag below the attachments (feet).
 */
function calcLevelSpanSagAt(tension, linearWeight, spanLength, distance) {
  const catenaryParam = tension / linearWeight;
  // The sag is the difference of two cosh terms, written as a product of sinh terms
  return 2 * catenaryParam * Math.sinh(distance / (2 * catenaryParam)) * Math.sinh((spanLength - distance) / (2 * catenaryParam));
}

/**
 * Describes a condition the sag table does not cover, so the user knows which row to author.
 * @param {string} wireSpec - The wire spec that was looked in.
 * @param {'surveyed'|'scenario'} label - Which of the two conditions is missing.
 * @param {{temperature: number, ice: number, wind: number, k: number, wireCondition: string}} condition - The condition.
 * @param {number} tabulatedRulingSpan - The tabulated ruling span that was searched (feet).
 * @returns {string} The description.
 */
function describeMissingRow(wireSpec, label, condition, tabulatedRulingSpan) {
  const { temperature, ice, wind, k, wireCondition } = condition;
  return (
    `the ${wireSpec} sag table has no ${wireCondition.toLowerCase()} tension for the ${label} condition ` +
    `(${temperature}°F, ${ice}" ice, ${wind} lb/ft² wind, ${k} lb/ft K) at its ${tabulatedRulingSpan} ft ruling span.`
  );
}

/**
 * Reads a marker attribute
 * @param {Object} marker - The marker.
 * @param {string} key - The attribute name.
 * @returns {*} The attribute's value, or null when it has none.
 */
function readAttribute(marker, key) {
  const value = marker?.[key];
  if (value != null && typeof value === 'object') return PickAnAttribute(marker, key) ?? null;
  return value ?? null;
}

/**
 * Reads a marker attribute as a number.
 * @param {Object} marker - The marker.
 * @param {string} key - The attribute name.
 * @returns {?number} The number, or null when the attribute is absent, blank, or not numeric.
 */
function readNumericAttribute(marker, key) {
  return toNumberOrNull(readAttribute(marker, key));
}

/**
 * Reads a wire condition attribute, defaults to 'Final'
 * @param {Object} marker - The marker.
 * @param {string} key - The attribute name.
 * @returns {'Initial'|'Final'} The wire condition.
 */
function readWireCondition(marker, key) {
  const value = `${readAttribute(marker, key) ?? ''}`.trim().toLowerCase();
  return value == 'initial' ? 'Initial' : SCENARIO_DEFAULTS.wireCondition;
}

/**
 * Casts a sag table cell or marker attribute to a number
 * @param {*} value - The value.
 * @returns {?number} The number, or null when the value is blank or not numeric.
 */
function toNumberOrNull(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}
